import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { addAdvisorSymbolContext, addWatchlistSymbol, clearAdvisorEntryGateScans, createPaperPosition, deleteAdvisorEntryGateScan, exportAdvisorEntryGateScans, getAdvisorEntryGateScans, getSignals, runMarketScanner, getAutoEntryStrategy, formatApiError, getInstrumentSearch, getRuntimeResources, getBotStatus } from "../api";
import SignalBadge from "../components/SignalBadge";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { downloadCsv } from "../utils/csv";

const actionRank = { "STRONG BUY": 5, BUY: 4, HOLD: 3, SELL: 2, "STRONG SELL": 1 };

const selectStyle = { background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 7, padding: "9px 10px", fontSize: 11, fontFamily: "'DM Mono',monospace", outline: "none" };
const smallInputStyle = { width: 78, background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "7px 8px", fontSize: 11, fontFamily: "'DM Mono',monospace", outline: "none" };
const inputWrapStyle = { display: "flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: 10 };
const SCANNER_CACHE_KEY = "algoTrade.lastScannerResults.v1";
const normalizeScannerRow = (r) => ({
  ...r,
  action: r.action || (r.recommendation === "TRENDING_UP" ? "BUY" : r.recommendation === "TRENDING_DOWN" ? "SELL" : "HOLD"),
  score: Number.isFinite(r.trendScore) ? Number(r.trendScore.toFixed(3)) : Number(r.score || 0),
  price: r.price || r.lastPrice,
  reasons: Array.isArray(r.reasons) ? r.reasons : [r.reason || r.recommendation || "Quote scan"],
});
const formatMb = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (n >= 1024) return `${(n / 1024).toFixed(n >= 10240 ? 0 : 1)} GB`;
  return `${n} MB`;
};
const formatPct = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(1)}%` : "—";
};
export default function Scanner({ botStatus, onRefresh, onOpenSymbol }) {
  const isMobile = useMediaQuery("(max-width: 760px)");
  const [results, setResults] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [scanProgressText, setScanProgressText] = useState("");
  const [sortKey, setSortKey] = useState("score");
  const [filter, setFilter] = useState("ALL");
  const [resultView, setResultView] = useState("rows");
  const [scanError, setScanError] = useState(null);
  const [scanInfo, setScanInfo] = useState(null);
  const [scanMeta, setScanMeta] = useState(null);
  const [resultsSource, setResultsSource] = useState(null);
  const [cachedScanSavedAt, setCachedScanSavedAt] = useState(null);
  const [scanMode, setScanMode] = useState("market");
  const [syncBackendDefaults, setSyncBackendDefaults] = useState(true);
  const [showAutoStrategy, setShowAutoStrategy] = useState(false);
  const [direction, setDirection] = useState("up");
  const [trendingOnly, setTrendingOnly] = useState(true);
  const [autoPaper, setAutoPaper] = useState(true);
  const [limit, setLimit] = useState(60);
  const [universeLimit, setUniverseLimit] = useState("");
  const manualControlsTouched = useRef(false);
  const universeLimitTouched = useRef(false);
  const [universeSource, setUniverseSource] = useState("nifty500_volume");
  const [screenerMode, setScreenerMode] = useState("blend");
  const [applyScanner, setApplyScanner] = useState(true);
  const [intradayScreeners, setIntradayScreeners] = useState({
    intraday_recovery: true,
    momentum_burst: true,
    bullish_crossover: true,
    open_low: true,
  });
  const [minChangePct, setMinChangePct] = useState(0);
  const [maxChangePct, setMaxChangePct] = useState(0);
  const [minStockPrice, setMinStockPrice] = useState(0);
  const [minVolume, setMinVolume] = useState("");
  const [dynamicMinVolume, setDynamicMinVolume] = useState(false);
  const [minLastCandleVolume, setMinLastCandleVolume] = useState(10000);
  const [minTrendScore, setMinTrendScore] = useState(0);
  const [minRelativeVolume, setMinRelativeVolume] = useState(0);
  const [minAverageVolume, setMinAverageVolume] = useState(500000);
  const [rsiMode, setRsiMode] = useState("all");
  const [macdPositive, setMacdPositive] = useState(false);
  const [mercuryPositive, setMercuryPositive] = useState(false);
  const [scoreHistoryLimit, setScoreHistoryLimit] = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [addingSymbol, setAddingSymbol] = useState(null);
  const [contextAddingSymbol, setContextAddingSymbol] = useState(null);
  const [contextStatus, setContextStatus] = useState(null);
  const [paperBuying, setPaperBuying] = useState(null);
  const [manualPriceSymbol, setManualPriceSymbol] = useState(null);
  const [manualPrice, setManualPrice] = useState("");
  const [aiGateHistory, setAiGateHistory] = useState([]);
  const [aiGateHistoryMeta, setAiGateHistoryMeta] = useState(null);
  const [aiGateHistoryLoading, setAiGateHistoryLoading] = useState(false);
  const [aiGateHistoryExporting, setAiGateHistoryExporting] = useState(false);
  const [aiGateHistoryError, setAiGateHistoryError] = useState(null);
  const [autoStrategy, setAutoStrategy] = useState(null);
  const [liveResources, setLiveResources] = useState(null);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState(null);
  // Symbol search for watchlist
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef(null);

  const touchManualControls = useCallback(() => {
    manualControlsTouched.current = true;
    setSyncBackendDefaults(false);
  }, []);

  useEffect(() => {
    if (!syncBackendDefaults || manualControlsTouched.current) return;
    const numberOr = (value, fallback) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    };
    const source = botStatus?.scannerUniverseSource || "nifty500_volume";
    const configuredLimit = numberOr(botStatus?.scannerLimit, 60);
    const configuredUniverse = numberOr(botStatus?.trendingUniverseLimit, 500);
    const configuredScreeners = String(Array.isArray(botStatus?.intradayScreeners) ? botStatus.intradayScreeners.join(",") : "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const screenerSet = new Set(configuredScreeners);

    setDirection("up");
    setTrendingOnly(true);
    setAutoPaper(!!(botStatus?.autoTradeFromScanner && botStatus?.autoPaperTradeFromScanner !== false));
    setLimit(Math.min(500, Math.max(10, configuredLimit)));
    if (Number.isFinite(configuredUniverse) && configuredUniverse >= 50) setUniverseLimit(configuredUniverse);
    setUniverseSource(source);
    setScreenerMode(botStatus?.intradayScreenerMode || "blend");
    setApplyScanner(botStatus?.scannerApplyRanking !== false);
    setIntradayScreeners((prev) => {
      if (!configuredScreeners.length) return prev;
      return {
        intraday_recovery: screenerSet.has("intraday_recovery"),
        momentum_burst: screenerSet.has("momentum_burst"),
        bullish_crossover: screenerSet.has("bullish_crossover"),
        open_low: screenerSet.has("open_low"),
      };
    });
    setMinChangePct(numberOr(botStatus?.scannerAutoTradeMinChangePct, 0));
    setMaxChangePct(numberOr(botStatus?.scannerAutoTradeMaxChangePct, 0));
    setMinStockPrice(numberOr(botStatus?.minStockPrice, 0));
    setMinVolume(numberOr(botStatus?.scannerMinVolume, 0));
    setDynamicMinVolume(botStatus?.scannerDynamicVolumeEnabled === true);
    setMinLastCandleVolume(numberOr(botStatus?.scannerMinLastCandleVolume, 10000));
    setMinTrendScore(numberOr(botStatus?.scannerAutoTradeMinScore, 0));
    setMinRelativeVolume(numberOr(botStatus?.scannerAutoTradeMinRvol, 0));
    setMinAverageVolume(numberOr(botStatus?.scannerAutoTradeMinAverageVolume, 500000));
    setRsiMode(botStatus?.scannerAutoTradeMinRsi || botStatus?.scannerAutoTradeMaxRsi ? "bullish" : "all");
    setMacdPositive(botStatus?.scannerAutoTradeRequireMacdPositive === true);
    setMercuryPositive(botStatus?.scannerAutoTradeRequireMercuryPositive === true);
    setScoreHistoryLimit(Math.max(200, configuredLimit));
  }, [botStatus, syncBackendDefaults]);

  useEffect(() => {
    const configured = Number(botStatus?.trendingUniverseLimit);
    if (syncBackendDefaults && !manualControlsTouched.current && !universeLimitTouched.current && Number.isFinite(configured) && configured >= 50) {
      setUniverseLimit(configured);
    }
  }, [botStatus?.trendingUniverseLimit, syncBackendDefaults]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(SCANNER_CACHE_KEY);
      if (!raw) return;
      const cached = JSON.parse(raw);
      if (Array.isArray(cached?.results)) {
        setResults(cached.results);
        setResultsSource("cache");
      }
      if (cached?.scanInfo) setScanInfo(cached.scanInfo);
      if (cached?.scanMeta) setScanMeta(cached.scanMeta);
      if (cached?.scanMode) setScanMode(cached.scanMode);
      if (cached?.savedAt) {
        setCachedScanSavedAt(cached.savedAt);
        if (!cached?.scanInfo) setScanInfo(`Showing previous scan from ${new Date(cached.savedAt).toLocaleString("en-IN")}.`);
      }
    } catch {}
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAutoEntryStrategy()
      .then((data) => {
        if (!cancelled) setAutoStrategy(data);
      })
      .catch(() => {
        if (!cancelled) setAutoStrategy(null);
      });
    return () => {
      cancelled = true;
    };
  }, [botStatus?.lastAutoScannerRun?.finishedAt, botStatus?.autoTradeFromScanner]);

  const refreshResources = useCallback(async () => {
    setResourcesLoading(true);
    setResourcesError(null);
    try {
      setLiveResources(await getRuntimeResources({ storage: 1 }));
    } catch (err) {
      setResourcesError(formatApiError(err?.raw || err));
    }
    setResourcesLoading(false);
  }, []);

  const persistScan = useCallback((payload) => {
    try {
      window.localStorage.setItem(SCANNER_CACHE_KEY, JSON.stringify({ ...payload, savedAt: new Date().toISOString() }));
    } catch {}
  }, []);

  // Preview: only show manual watchlist items (not the 65 BOD stocks)
  // botStatus.manualWatchlist = items added by the user via the watchlist UI
  const manualWatchlistRows = useMemo(() => {
    const manual = botStatus?.manualWatchlist || [];
    return manual.map((w) => ({
      symbol: w.symbol, instrumentKey: w.instrumentKey,
      action: "HOLD", score: 0, price: null, lastPrice: null,
      changePct: null, volume: null, recommendation: "WATCHLIST",
      isTrending: false, source: "manual",
      reasons: ["Click 'Run Live Market Scanner' to get live data."],
      _placeholder: true,
    }));
  }, [botStatus?.manualWatchlist]);

  const backendAutoRows = useMemo(() => {
    const rows = botStatus?.lastAutoScannerRun?.rows;
    return Array.isArray(rows) ? rows.map(normalizeScannerRow) : [];
  }, [botStatus?.lastAutoScannerRun?.finishedAt, botStatus?.lastAutoScannerRun?.rows]);
  const backendAutoScanMeta = useMemo(() => {
    const run = botStatus?.lastAutoScannerRun;
    if (!run) return null;
    return {
      diagnostics: run.diagnostics || null,
      autoTradeDiagnostics: run,
      autoPaperDiagnostics: run,
      source: "backend_autoscan",
    };
  }, [botStatus?.lastAutoScannerRun]);

  useEffect(() => {
    if (!backendAutoRows.length || scanning || scanMode !== "market") return;
    const backendFinished = Date.parse(botStatus?.lastAutoScannerRun?.finishedAt || "");
    const cachedSaved = Date.parse(cachedScanSavedAt || "");
    const shouldAdoptBackend =
      results.length === 0 ||
      (resultsSource === "cache" && Number.isFinite(backendFinished) && (!Number.isFinite(cachedSaved) || backendFinished > cachedSaved));
    if (!shouldAdoptBackend) return;
    const finishedLabel = botStatus?.lastAutoScannerRun?.finishedAt
      ? ` from ${new Date(botStatus.lastAutoScannerRun.finishedAt).toLocaleString("en-IN")}`
      : "";
    setResults(backendAutoRows);
    setScanMeta(backendAutoScanMeta);
    setResultsSource("backend");
    setScanInfo(`Showing ${backendAutoRows.length} backend autoscan row${backendAutoRows.length === 1 ? "" : "s"}${finishedLabel}.`);
  }, [backendAutoRows, backendAutoScanMeta, botStatus?.lastAutoScannerRun?.finishedAt, cachedScanSavedAt, results.length, resultsSource, scanMode, scanning]);

  const effectiveScanMeta = scanMeta || (results.length === 0 ? backendAutoScanMeta : null);
  const effectiveResults = results.length > 0 ? results : backendAutoRows.length > 0 ? backendAutoRows : manualWatchlistRows;
  const hasScanned = results.length > 0 || backendAutoRows.length > 0;
  const showingBackendAutoScan = resultsSource === "backend" || (results.length === 0 && backendAutoRows.length > 0);
  const syncBackendScanRows = useCallback(async () => {
    setScanError(null);
    let run = botStatus?.lastAutoScannerRun || null;
    try {
      const freshStatus = await getBotStatus();
      run = freshStatus?.lastAutoScannerRun || run;
    } catch (err) {
      if (!run) {
        setScanError(formatApiError(err?.raw || err));
        return;
      }
    }
    const rows = Array.isArray(run?.rows) ? run.rows.map(normalizeScannerRow) : backendAutoRows;
    const meta = run ? {
      diagnostics: run.diagnostics || null,
      autoTradeDiagnostics: run,
      autoPaperDiagnostics: run,
      source: "backend_autoscan",
    } : backendAutoScanMeta;
    if (!rows.length) {
      setScanInfo("No backend autoscan rows are available yet.");
      return;
    }
    const finishedAt = run?.finishedAt;
    const info = `Synced ${rows.length} backend autoscan row${rows.length === 1 ? "" : "s"}${finishedAt ? ` from ${new Date(finishedAt).toLocaleString("en-IN")}` : ""}.`;
    setResults(rows);
    setResultsSource("backend");
    setScanMeta(meta);
    setScanMode("market");
    setScanInfo(info);
    persistScan({ results: rows, scanInfo: info, scanMeta: meta, scanMode: "market" });
    if (onRefresh) await onRefresh();
  }, [backendAutoRows, backendAutoScanMeta, botStatus?.lastAutoScannerRun, onRefresh, persistScan]);
  const isChartinkRow = (row) =>
    String(row?.source || "").toLowerCase().includes("chartink") ||
    (row?.screenerTags || []).includes("chartink") ||
    (row?.screenerLabels || []).some((label) => String(label || "").toLowerCase().includes("chartink"));
  const formatScreenerLabel = (row, label) => {
    const text = String(label || "").trim();
    if (!text) return "";
    if (text.toLowerCase().startsWith("chartink")) return text;
    return `${isChartinkRow(row) ? "Chartink" : "Upstox"} ${text}`;
  };
  const visibleScreenerLabel = (row) => {
    const labels = (row?.screenerLabels || []).map((label) => formatScreenerLabel(row, label)).filter(Boolean);
    if (!labels.length) return "";
    if (!isChartinkRow(row)) return labels.length > 1 ? `Screeners: ${labels[0]} +${labels.length - 1}` : labels[0];
    const first = labels[0].replace(/^Chartink\s*:?\s*/i, "");
    return labels.length > 1 ? `Chartink: ${first} +${labels.length - 1}` : `Chartink: ${first}`;
  };

  const handleSearch = useCallback((q) => {
    setSearchQ(q);
    clearTimeout(searchTimer.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await getInstrumentSearch(q.trim());
        setSearchResults(data?.matches || []);
      } catch { setSearchResults([]); }
      setSearchLoading(false);
    }, 300);
  }, []);

  const runScan = useCallback(async () => {
    if (scanning) return;
    setScanning(true);
    setScanError(null);
    setScanInfo(null);
    setScanMeta(null);
    setProgress(0);
    setScanProgressText("");
    const tick = scanMode === "signals"
      ? setInterval(() => setProgress((p) => Math.min(p + 5, 88)), 200)
      : null;
    try {
      if (scanMode === "signals") {
        const data = await getSignals();
        const signals = Array.isArray(data) ? data : [];
        if (signals.length === 0) {
          setScanError("No signal data yet. Bot needs ~60 minutes of live market ticks per symbol to generate signals. Market hours: 09:15–15:30 IST.");
        } else {
          const active = signals.filter(s => s.action !== "HOLD" || s.score !== 0).length;
          setScanInfo(`${signals.length} symbols tracked${active ? `, ${active} with active signals` : " — all HOLD (needs more candle data)"}.`);
        }
        setResults(signals.map(s => ({
          ...s, price: s.price || null, lastPrice: s.price || null,
          changePct: s.changePct ?? null, volume: s.volume ?? null,
          recommendation: s.action === "BUY" ? "TRENDING_UP" : s.action === "SELL" ? "TRENDING_DOWN" : "NEUTRAL",
          isTrending: s.action !== "HOLD",
          reasons: Array.isArray(s.reasons) ? s.reasons : [s.reason || "Strategy signal"],
        })));
        setResultsSource("manual");
        persistScan({
          results: signals.map(s => ({
            ...s, price: s.price || null, lastPrice: s.price || null,
            changePct: s.changePct ?? null, volume: s.volume ?? null,
            recommendation: s.action === "BUY" ? "TRENDING_UP" : s.action === "SELL" ? "TRENDING_DOWN" : "NEUTRAL",
            isTrending: s.action !== "HOLD",
            reasons: Array.isArray(s.reasons) ? s.reasons : [s.reason || "Strategy signal"],
          })),
          scanInfo: `${signals.length} symbols from bot signal scan.`,
          scanMeta: null,
          scanMode,
        });
      } else {
        const parsedUniverseLimit = Number(universeLimit);
        const data = await runMarketScanner({
          direction, trendingOnly, autoPaper: universeSource === "nifty500_volume" ? autoPaper : false, limit,
          ...(Number.isFinite(parsedUniverseLimit) && parsedUniverseLimit >= 50 ? { universeLimit: parsedUniverseLimit } : {}),
          universeSource, minChangePct, maxChangePct, minStockPrice, minTrendScore,
          dynamicMinVolume,
          minVolume: dynamicMinVolume ? undefined : Number(minVolume || 0),
          minLastCandleVolume, minRelativeVolume, minAverageVolume, rsiMode, macdPositive, mercuryPositive, scoreHistoryLimit, screenerMode, applyScanner,
          screeners: Object.entries(intradayScreeners).filter(([, enabled]) => enabled).map(([key]) => key).join(","),
        }, (job) => {
          const pct = Number(job?.progress?.pct);
          if (Number.isFinite(pct)) setProgress(Math.max(0, Math.min(100, pct)));
          const message = job?.progress?.message || job?.progress?.stage || job?.status;
          const pid = job?.workerPid || job?.progress?.workerPid;
          const suffix = pid ? ` · worker ${pid}` : "";
          const safeSuffix = pid ? ` - worker ${pid}` : "";
          if (message) setScanProgressText(`${message}${safeSuffix}`);
        });
        const rows = (data?.rows || []).map(normalizeScannerRow);
        const nextScanMeta = data?.diagnostics ? {
          diagnostics: data.diagnostics,
          autoPaperDiagnostics: data.autoPaperDiagnostics || null,
          autoTradeDiagnostics: data.autoTradeDiagnostics || null,
        } : null;
        setScanMeta(nextScanMeta);
        const chartink = data?.diagnostics?.chartink || null;
        const chartinkFirstError = chartink?.errors?.[0]?.message;
        const chartinkText = chartink
          ? chartink.enabled
            ? ` Chartink: ${data?.diagnostics?.chartinkUniverseSize || 0} resolved from ${chartink.symbolCount || 0} symbol(s)${chartink.cacheHit ? " (cache)" : ""}${chartink.errors?.length ? `, ${chartink.errors.length} screener error(s)${chartinkFirstError ? `, first: ${chartinkFirstError}` : ""}` : ""}.`
            : " Chartink: disabled."
          : "";
        if (rows.length === 0) {
          const reason = data?.diagnostics?.noDataReason;
          const survivorPath = data?.diagnostics?.filterSurvivors;
          const survivorText = survivorPath
            ? ` Filter path: ${Object.entries(survivorPath).map(([key, value]) => `${key}=${value}`).join(" -> ")}.`
            : "";
          const techCounts = data?.diagnostics?.technicalCountsBeforeFilters;
          const techText = techCounts
            ? ` Technicals across priced rows (historical values exist only for enriched rows): MACD+=${techCounts.macdPositive || 0}, Mercury+=${techCounts.mercuryPositive || 0}, both=${techCounts.macdAndMercuryPositive || 0}, trending=${techCounts.trending || 0}, trendingUp=${techCounts.trendingUp || 0}.`
            : "";
          const effectiveFilters = data?.diagnostics?.filters;
          const effectiveFilterText = effectiveFilters
            ? ` Effective filters: direction=${effectiveFilters.direction}, trendingOnly=${effectiveFilters.trendingOnly}, minChange=${effectiveFilters.minChangePct}, maxChange=${effectiveFilters.maxChangePct}, minPrice=${effectiveFilters.minStockPrice}, minDayVolume=${effectiveFilters.minVolume}, min5mVolume=${effectiveFilters.minLastCandleVolume}, minScore=${effectiveFilters.minTrendScore}, minRVOL=${effectiveFilters.minRelativeVolume}, min20dAvg=${effectiveFilters.minAverageVolume}, RSI=${effectiveFilters.rsiMode}, MACD+=${effectiveFilters.macdPositive}, Mercury+=${effectiveFilters.mercuryPositive}, screenerMode=${effectiveFilters.screenerMode}.`
            : "";
          const dailyPath = data?.diagnostics?.dailyGateSurvivorsByFilter;
          const dailyPathText = dailyPath
            ? ` Daily gate path: ${Object.entries(dailyPath).map(([key, value]) => `${key}=${value}`).join(" -> ")}.`
            : "";
          const quotePath = data?.diagnostics?.quoteGateSurvivorsByFilter;
          const quotePathText = quotePath
            ? ` Quote gate path: ${Object.entries(quotePath).map(([key, value]) => `${key}=${value}`).join(" -> ")}.`
            : "";
          const dailyFailures = data?.diagnostics?.dailyGateIndependentFailures;
          const dailyFailureText = dailyFailures
            ? ` Independent daily failures: ${Object.entries(dailyFailures).map(([key, value]) => `${key}=${value}`).join(", ")}.`
            : "";
          const trendGate = data?.diagnostics?.trendGateDetails;
          const trendGateText = trendGate
            ? ` Trend checks (${trendGate.evaluated || 0} enriched): change-pass=${trendGate.changePass || 0}, day-volume-pass=${trendGate.dayVolumePass || 0}, score-pass=${trendGate.scorePass || 0}, composite-pass=${trendGate.compositePass || 0}.`
            : "";
          const historyCache = data?.diagnostics?.historyCache;
          const historyCacheText = historyCache
            ? ` History cache: RAM=${historyCache.memoryHits || 0}, disk=${historyCache.diskHits || 0}, derived=${historyCache.derivedMetricHits || 0}, derivedWrites=${historyCache.derivedMetricWrites || 0}, Upstox=${historyCache.apiFetches || 0}, failures=${historyCache.apiFailures || 0}, memoryEntries=${historyCache.memoryEntries || 0}, memoryCache=${historyCache.keepMemoryCache !== false}.`
            : "";
          const worker = data?.diagnostics?.worker;
          const workerText = worker
            ? ` Worker: pid=${worker.pid || "?"}, ${Math.round(Number(worker.durationMs || 0) / 1000)}s, RSS ${worker.memoryStartMb?.rss ?? "?"}->${worker.memoryEndMb?.rss ?? "?"} MB.`
            : "";
          const manualRequest = data?.diagnostics?.manualRequest;
          const manualRequestText = manualRequest
            ? ` Manual request honored: direction=${manualRequest.direction}, trendingOnly=${manualRequest.trendingOnly}; auto-entry requested=${manualRequest.autoEntryRequested}, auto-entry filters applied to scan=${manualRequest.autoEntryFiltersAppliedToScan}.`
            : "";
          const detail = data?.diagnostics
            ? ` Diagnostics: instruments=${data.diagnostics.instrumentCount || 0}, universe=${data.diagnostics.universeSize || 0}, quotes=${data.diagnostics.quoteCount || 0}, priced=${data.diagnostics.pricedCount || 0}.`
            : "";
          const historyStatuses = data?.diagnostics?.historicalDataStatusCounts;
          const activityStatuses = data?.diagnostics?.intradayActivityStatusCounts;
          const pipelineText = data?.diagnostics
            ? ` Pipeline: quote-gate=${data.diagnostics.quoteGateSurvivors ?? "?"}, history=${data.diagnostics.enrichmentCandidates ?? "?"} (${historyStatuses ? Object.entries(historyStatuses).map(([k, v]) => `${k}:${v}`).join(",") : "no status"}), daily-gate=${data.diagnostics.dailyGateSurvivors ?? "?"}, 5m=${data.diagnostics.intradayActivityCandidates ?? "?"} (${activityStatuses ? Object.entries(activityStatuses).map(([k, v]) => `${k}:${v}`).join(",") : "no status"}), 5m-volume-pass=${data.diagnostics.lastCandleVolumeSurvivors ?? "?"}.`
            : "";
          setScanError(
            "Market scan returned 0 stocks with live data. " +
            (reason
              ? reason
              : botStatus?.marketOpen === false
                ? "Market is currently closed, but manual Upstox quote scans are still allowed. Try relaxing filters if quotes are available but no row passes."
                : "Upstox quote API returned no data. Check your Upstox session is valid, or try relaxing filters (set Min% to 0).") +
            detail +
            pipelineText +
            chartinkText +
            historyCacheText +
            workerText +
            effectiveFilterText +
            manualRequestText +
            quotePathText +
            dailyPathText +
            dailyFailureText +
            trendGateText +
            survivorText +
            techText
          );
        } else {
          const universe = data?.diagnostics?.universeSize || data?.count || rows.length;
          const quotes = data?.diagnostics?.quoteCount || rows.length;
          const enriched = data?.diagnostics?.enrichedCount;
          const enrichmentCandidates = data?.diagnostics?.enrichmentCandidates;
          const rejected = data?.autoPaperDiagnostics?.rejected?.[0];
          const effectiveAutoPaper = autoPaper && universeSource === "nifty500_volume";
          const autoText = data?.autoPaperEntries?.length
            ? ` Auto paper entries: ${data.autoPaperEntries.map((e) => e.symbol).join(", ")}.`
            : effectiveAutoPaper ? ` No auto paper entry met all risk/quality gates${rejected ? ` (${rejected.symbol}: ${rejected.reason})` : ""}.` : "";
          const screenerCount = data?.diagnostics?.screenerUniverseSize || 0;
          const computedMatches = data?.diagnostics?.computedScreenerMatches || 0;
          const sourceCounts = data?.diagnostics?.sourceCountsAfterFilters || null;
          const sourceText = sourceCounts
            ? ` Source after filters: ${Object.entries(sourceCounts).map(([k, v]) => `${k} ${v}`).join(", ")}.`
            : "";
          const quotedSourceCounts = data?.diagnostics?.sourceQuoteCounts || null;
          const quotedSourceText = quotedSourceCounts
            ? ` Fresh quote coverage: ${Object.entries(quotedSourceCounts).map(([k, v]) => `${k} ${v}`).join(", ")}; unquoted ${data?.diagnostics?.unquotedUniverseCount || 0}.`
            : "";
          const avoidedHistory = data?.diagnostics?.historicalRequestsAvoided;
          const optimizationText = Number.isFinite(Number(avoidedHistory))
            ? ` Exact live gates avoided ${avoidedHistory} unnecessary historical fetches.`
            : "";
          const historyCache = data?.diagnostics?.historyCache;
          const historyCacheText = historyCache
            ? ` History cache: RAM ${historyCache.memoryHits || 0}, disk ${historyCache.diskHits || 0}, derived ${historyCache.derivedMetricHits || 0}, derivedWrites ${historyCache.derivedMetricWrites || 0}, Upstox ${historyCache.apiFetches || 0}, failures ${historyCache.apiFailures || 0}, memoryCache ${historyCache.keepMemoryCache !== false}.`
            : "";
          const worker = data?.diagnostics?.worker;
          const workerText = worker
            ? ` Worker ${Math.round(Number(worker.durationMs || 0) / 1000)}s, RSS ${worker.memoryStartMb?.rss ?? "?"}->${worker.memoryEndMb?.rss ?? "?"} MB.`
            : "";
          const effectiveFilters = data?.diagnostics?.filters;
          const effectiveFilterText = effectiveFilters
            ? ` Effective filters: direction=${effectiveFilters.direction}, trendingOnly=${effectiveFilters.trendingOnly}, minChange=${effectiveFilters.minChangePct}, maxChange=${effectiveFilters.maxChangePct}, minPrice=${effectiveFilters.minStockPrice}, minDayVolume=${effectiveFilters.minVolume}, min5mVolume=${effectiveFilters.minLastCandleVolume}, minScore=${effectiveFilters.minTrendScore}, minRVOL=${effectiveFilters.minRelativeVolume}, min20dAvg=${effectiveFilters.minAverageVolume}.`
            : "";
          const dailyPath = data?.diagnostics?.dailyGateSurvivorsByFilter;
          const dailyPathText = dailyPath
            ? ` Daily gate path: ${Object.entries(dailyPath).map(([key, value]) => `${key}=${value}`).join(" -> ")}.`
            : "";
          const quotePath = data?.diagnostics?.quoteGateSurvivorsByFilter;
          const quotePathText = quotePath
            ? ` Quote gate path: ${Object.entries(quotePath).map(([key, value]) => `${key}=${value}`).join(" -> ")}.`
            : "";
          const trendGate = data?.diagnostics?.trendGateDetails;
          const trendGateText = trendGate
            ? ` Trend checks (${trendGate.evaluated || 0} enriched): change-pass=${trendGate.changePass || 0}, day-volume-pass=${trendGate.dayVolumePass || 0}, score-pass=${trendGate.scorePass || 0}, composite-pass=${trendGate.compositePass || 0}.`
            : "";
          const survivorPath = data?.diagnostics?.filterSurvivors;
          const survivorText = survivorPath
            ? ` Final filter path: ${Object.entries(survivorPath).map(([key, value]) => `${key}=${value}`).join(" -> ")}.`
            : "";
          const components = data?.diagnostics?.universeSourceComponents || null;
          const componentText = components
            ? ` Universe components: Nifty/Upstox liquid ${components.curatedAndUpstoxLiquid || 0}, GitHub/Yahoo ${components.externalYahoo || 0}, Chartink ${components.chartink || 0}, Upstox BOD ${components.upstoxBodSlice || 0}, Upstox screeners ${components.upstoxIntradayScreeners || 0}, union ${components.union || 0}.`
            : "";
          const chartinkReturned = data?.diagnostics?.chartinkReturnedCount;
          const chartinkReturnedText = Number.isFinite(Number(chartinkReturned))
            ? ` Chartink returned rows: ${chartinkReturned}.`
            : "";
          const fallbackText = computedMatches ? `, ${computedMatches} computed intraday screener matches` : "";
          const screenerText = screenerCount ? `, including ${screenerCount} external intraday screener symbols` : fallbackText;
          const scannerText = applyScanner
            ? `enriched ${enriched ?? 0}/${enrichmentCandidates ?? 0} with historical RSI/RVOL/MACD/Mercury; showing ${rows.length} ranked rows`
            : `scanner ranking disabled; showing ${rows.length} raw quote rows from the selected source union`;
          setScanInfo(`Fetched ${quotes} live quotes from ${universe}-stock universe${screenerText}; ${scannerText}.${chartinkText}${componentText}${quotedSourceText}${optimizationText}${historyCacheText}${workerText}${effectiveFilterText}${quotePathText}${dailyPathText}${trendGateText}${survivorText}${sourceText}${chartinkReturnedText}${autoText}`);
        }
        setResults(rows);
        setResultsSource("manual");
        persistScan({ results: rows, scanInfo: rows.length ? `Showing ${rows.length} ranked rows from the last Upstox market scan.` : scanInfo, scanMeta: nextScanMeta, scanMode });
        if ((data?.autoPaperEntries?.length || data?.autoTradeEntries?.length) && onRefresh) {
          await onRefresh();
        }
      }
    } catch (e) {
      setScanError(formatApiError(e?.raw || e));
    }
    if (tick) clearInterval(tick);
    setProgress(100);
    setScanProgressText("");
    setTimeout(() => { setScanning(false); setProgress(0); }, 400);
  }, [applyScanner, autoPaper, direction, dynamicMinVolume, intradayScreeners, limit, macdPositive, maxChangePct, mercuryPositive, minAverageVolume, minChangePct, minLastCandleVolume, minRelativeVolume, minStockPrice, minTrendScore, minVolume, rsiMode, scanMode, scoreHistoryLimit, screenerMode, trendingOnly, universeLimit, universeSource, botStatus?.marketOpen, scanning, onRefresh, persistScan, scanInfo]);

  useEffect(() => {
    if (!autoRefresh || !botStatus?.marketOpen || scanMode !== "market" || results.length === 0) return undefined;
    const t = setInterval(() => {
      runScan();
    }, 60000);
    return () => clearInterval(t);
  }, [autoRefresh, botStatus?.marketOpen, scanMode, results.length, runScan]);

  const addToWatchlist = async (symbol, instrumentKey) => {
    setAddingSymbol(symbol);
    setScanError(null);
    try {
      await addWatchlistSymbol({ symbol, instrumentKey });
      if (onRefresh) await onRefresh();
    } catch (e) { setScanError(formatApiError(e?.raw || e)); }
    setAddingSymbol(null);
  };

  const addScannerRowToContext = async (row) => {
    if (!row?.symbol) return;
    setContextAddingSymbol(row.symbol);
    setContextStatus(null);
    try {
      const payload = await addAdvisorSymbolContext({
        symbol: row.symbol,
        instrumentKey: row.instrumentKey,
        scannerRow: {
          ...row,
          addedFrom: "scanner_ui",
          addedAt: new Date().toISOString(),
          reasonText: [
            ...(row.screenerLabels || []).map((label) => formatScreenerLabel(row, label)),
            ...(row.reasons || [row.reason || ""]),
          ].filter(Boolean).join(" | "),
        },
      });
      setContextStatus({
        type: "ok",
        text: payload?.reused
          ? `${row.symbol} scanner context updated from saved candles`
          : `${row.symbol} scanner context added with candles`,
      });
    } catch (err) {
      setContextStatus({ type: "error", text: formatApiError(err?.raw || err) || `Failed to add ${row.symbol}` });
    } finally {
      setContextAddingSymbol(null);
    }
  };

  const paperBuy = async (row, priceOverride) => {
    const price = priceOverride != null ? priceOverride : (row.price || row.lastPrice);
    if (!price || price <= 0) {
      setManualPriceSymbol(row.symbol);
      setManualPrice("");
      return;
    }
    setPaperBuying(row.symbol);
    setScanError(null);
    try {
      const scannerReasons = [
        ...(row.screenerLabels || []).map((label) => `Screener: ${label}`),
        ...(row.reasons || []),
        row.reason,
        row.recommendation ? `Recommendation: ${row.recommendation}` : null,
        row.macdPositive ? "MACD positive" : null,
        row.mercuryPositive ? "Mercury positive" : null,
        Number.isFinite(row.relativeVolume) ? `Time-adjusted relative volume ${Number(row.relativeVolume).toFixed(2)}x` : null,
      ].filter(Boolean);
      await createPaperPosition({
        symbol: row.symbol,
        instrumentKey: row.instrumentKey,
        price,
        entryReason: scannerReasons.length > 0 ? `Manual | ${scannerReasons.join(" | ")}` : "Manual",
        entryReasons: ["Manual", ...scannerReasons],
        entryScore: row.trendScore ?? row.score ?? null,
        changePct: row.changePct ?? null,
        relativeVolume: row.relativeVolume ?? null,
        relativeVolumeRaw: row.relativeVolumeRaw ?? null,
        volumeElapsedFraction: row.volumeElapsedFraction ?? null,
        rsi14: row.rsi14 ?? null,
        macdPositive: !!row.macdPositive,
        macdBullishCross: !!row.macdBullishCross,
        mercuryPositive: !!row.mercuryPositive,
        mercuryScore: row.mercuryScore ?? null,
        screenerTags: Array.isArray(row.screenerTags) ? row.screenerTags : [],
        screenerLabels: Array.isArray(row.screenerLabels) ? row.screenerLabels : [],
      });
      if (onRefresh) await onRefresh();
      setManualPriceSymbol(null);
    } catch (e) { setScanError(formatApiError(e?.raw || e)); }
    setPaperBuying(null);
  };

  const localFiltered = [...effectiveResults].filter((r) => {
    const actionOk =
      filter === "ALL" || r.action === filter ||
      (filter === "BUY_ALL" && r.action?.includes("BUY")) ||
      (filter === "SELL_ALL" && r.action?.includes("SELL")) ||
      (filter === "TRENDING" && r.isTrending);
    if (!actionOk) return false;
    if (!hasScanned || scanMode !== "market") return true;
    const activeMinVolume = Number(effectiveScanMeta?.diagnostics?.filters?.minVolume ?? minVolume ?? 0);
    if (direction === "up" && Number(r.changePct || 0) <= 0) return false;
    if (direction === "down" && Number(r.changePct || 0) >= 0) return false;
    if (direction === "neutral" && Math.abs(Number(r.changePct || 0)) >= minChangePct) return false;
    if (trendingOnly && !r.isTrending) return false;
    if (Math.abs(Number(r.changePct || 0)) < minChangePct) return false;
    if (maxChangePct > 0 && Math.abs(Number(r.changePct || 0)) > maxChangePct) return false;
    if (minStockPrice > 0 && Number(r.lastPrice || r.price || 0) < minStockPrice) return false;
    if (Number(r.volume || 0) < activeMinVolume) return false;
    if (minLastCandleVolume > 0 && (!Number.isFinite(Number(r.lastCandleVolume)) || Number(r.lastCandleVolume) < minLastCandleVolume)) return false;
    if (Number(r.score || 0) < minTrendScore) return false;
    if (minRelativeVolume > 0 && Number(r.relativeVolume || 0) < minRelativeVolume) return false;
    if (minAverageVolume > 0 && Number(r.avgVolume20d || 0) < minAverageVolume) return false;
    if (rsiMode === "bullish" && r.rsi14 != null && (r.rsi14 < 45 || r.rsi14 > 70)) return false;
    if (rsiMode === "avoid_overbought" && r.rsi14 != null && r.rsi14 > 70) return false;
    if (rsiMode === "oversold" && !(r.rsi14 != null && r.rsi14 <= 40)) return false;
    if (macdPositive && !r.macdPositive) return false;
    if (mercuryPositive && !r.mercuryPositive) return false;
    return true;
  });

  const sorted = localFiltered
    .sort((a, b) => {
      if (sortKey === "score") return (b.score || 0) - (a.score || 0);
      if (sortKey === "action") return (actionRank[b.action] || 3) - (actionRank[a.action] || 3);
      if (sortKey === "price") return (b.price || 0) - (a.price || 0);
      if (sortKey === "changePct") return Math.abs(b.changePct || 0) - Math.abs(a.changePct || 0);
      if (sortKey === "volume") return (b.volume || 0) - (a.volume || 0);
      if (sortKey === "lastCandleVolume") return (b.lastCandleVolume || 0) - (a.lastCandleVolume || 0);
      if (sortKey === "relativeVolume") return (b.relativeVolume || 0) - (a.relativeVolume || 0);
      if (sortKey === "rsi14") return (b.rsi14 || 0) - (a.rsi14 || 0);
      if (sortKey === "symbol") return String(a.symbol || "").localeCompare(String(b.symbol || ""));
      return 0;
    });

  const aiGateRows = useMemo(() => {
    const diagnostics = effectiveScanMeta?.autoTradeDiagnostics || effectiveScanMeta?.autoPaperDiagnostics || botStatus?.lastAutoScannerRun || {};
    const gateRows = Array.isArray(diagnostics.aiGate) ? diagnostics.aiGate : [];
    const entryRows = Array.isArray(diagnostics.entries) ? diagnostics.entries : [];
    const rejectedRows = Array.isArray(diagnostics.rejected) ? diagnostics.rejected : [];
    const bySymbol = new Map();
    gateRows.forEach((row) => {
      const symbol = String(row?.symbol || "").toUpperCase();
      if (!symbol) return;
      bySymbol.set(symbol, {
        symbol,
        status: row.status || (row.approved ? "approved" : "blocked"),
        decision: row.decision || "-",
        confidence: row.confidence,
        reason: row.reason || row.summary || "",
        provider: row.provider || "",
        model: row.model || "",
        analyzedAt: row.analyzedAt || null,
        contextStats: row.contextStats || null,
      });
    });
    entryRows.forEach((row) => {
      const symbol = String(row?.symbol || "").toUpperCase();
      if (!symbol || bySymbol.has(symbol)) return;
      bySymbol.set(symbol, {
        symbol,
        status: "bought",
        decision: row.aiGate?.decision || "BUY",
        confidence: row.aiGate?.confidence,
        reason: row.aiGate?.reason || "Position created after auto-entry gates passed",
        provider: row.aiGate?.provider || "",
        model: row.aiGate?.model || "",
        analyzedAt: row.aiGate?.analyzedAt || null,
        contextStats: null,
      });
    });
    rejectedRows
      .filter((row) => String(row?.reason || "").toLowerCase().includes("ai entry gate"))
      .forEach((row) => {
        const symbol = String(row?.symbol || "").toUpperCase();
        if (!symbol || bySymbol.has(symbol)) return;
        bySymbol.set(symbol, {
          symbol,
          status: "blocked",
          decision: "REJECTED",
          confidence: null,
          reason: row.reason || "",
          provider: "",
          model: "",
          analyzedAt: null,
          contextStats: null,
        });
      });
    return [...bySymbol.values()];
  }, [botStatus?.lastAutoScannerRun, effectiveScanMeta]);

  const loadAiGateHistory = useCallback(async () => {
    setAiGateHistoryLoading(true);
    setAiGateHistoryError(null);
    try {
      const data = await getAdvisorEntryGateScans({ limit: 100 });
      setAiGateHistory(Array.isArray(data?.scans) ? data.scans : []);
      setAiGateHistoryMeta(data || null);
    } catch (err) {
      setAiGateHistoryError(err?.message || "Failed to load AI gate history");
    } finally {
      setAiGateHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (resultView === "aiHistory") loadAiGateHistory();
  }, [loadAiGateHistory, resultView, botStatus?.lastAutoScannerRun?.finishedAt]);

  const clearAiGateHistory = async () => {
    if (!window.confirm("Delete all past AI gate scan entries?")) return;
    setAiGateHistoryLoading(true);
    setAiGateHistoryError(null);
    try {
      await clearAdvisorEntryGateScans();
      await loadAiGateHistory();
    } catch (err) {
      setAiGateHistoryError(err?.message || "Failed to clear AI gate history");
    } finally {
      setAiGateHistoryLoading(false);
    }
  };

  const removeAiGateHistoryRow = async (id) => {
    if (!id) return;
    setAiGateHistoryError(null);
    try {
      await deleteAdvisorEntryGateScan(id);
      setAiGateHistory((rows) => rows.filter((row) => row.id !== id));
    } catch (err) {
      setAiGateHistoryError(err?.message || "Failed to delete AI gate row");
    }
  };

  const exportAiGateHistory = async () => {
    setAiGateHistoryExporting(true);
    setAiGateHistoryError(null);
    try {
      const data = await exportAdvisorEntryGateScans({ limit: 1000 });
      const stats = data?.stats || {};
      const countRows = (section, counts = {}) => Object.entries(counts || {}).map(([metric, value]) => ({
        section,
        metric,
        value,
      }));
      const summaryRows = [
        ["exported_at", data?.exportedAt],
        ["total_stored", data?.totalStored],
        ["exported_rows", data?.exportedRows],
        ["entries", stats.entries],
        ["blocked", stats.blocked],
        ["errors", stats.errors],
        ["buy_decisions", stats.buyDecisions],
        ["wait_decisions", stats.waitDecisions],
        ["reject_decisions", stats.rejectDecisions],
        ["avg_confidence", stats.avgConfidence],
      ].map(([metric, value]) => ({ section: "summary", metric, value }));
      const breakdownRows = [
        ...countRows("status_count", stats.byStatus),
        ...countRows("outcome_count", stats.byOutcome),
        ...countRows("decision_count", stats.byDecision),
        ...countRows("provider_count", stats.byProvider),
        ...countRows("model_count", stats.byModel),
      ];
      const detailRows = (Array.isArray(data?.rows) ? data.rows : []).map((row) => {
        const scanner = row.scanner || {};
        const candleStats = row.contextStats || {};
        return {
          section: "scan",
          symbol: row.symbol || "",
          stage: row.stage || "",
          status: row.status || "",
          outcome: row.outcome || "",
          decision: row.decision || "",
          confidence: row.confidence ?? "",
          provider: row.provider || "",
          model: row.model || "",
          source: row.source || "",
          trigger: row.trigger || "",
          scanner_score: scanner.score ?? "",
          scanner_change_pct: scanner.changePct ?? "",
          scanner_adjusted_rvol: scanner.relativeVolume ?? "",
          scanner_raw_rvol: scanner.relativeVolumeRaw ?? "",
          scanner_rsi14: scanner.rsi14 ?? "",
          scanner_last_price: scanner.lastPrice ?? "",
          daily_candles: candleStats.dailyCandles ?? "",
          hourly_candles: candleStats.hourlyCandles ?? "",
          fifteen_minute_candles: candleStats.fifteenMinuteCandles ?? "",
          five_minute_candles: candleStats.fiveMinuteCandles ?? "",
          risk_flags: Array.isArray(row.riskFlags) ? row.riskFlags.join("|") : "",
          reason_codes: Array.isArray(row.reasonCodes) ? row.reasonCodes.join("|") : "",
          reason: row.reason || row.error || "",
          analyzed_at: row.analyzedAt || "",
          created_at: row.createdAt || "",
        };
      });
      const ok = downloadCsv("ai-gate-scan-statistics", [...summaryRows, ...breakdownRows, ...detailRows]);
      if (!ok) setAiGateHistoryError("No AI gate history available to export");
    } catch (err) {
      setAiGateHistoryError(err?.message || "Failed to export AI gate statistics");
    } finally {
      setAiGateHistoryExporting(false);
    }
  };

  const Th = ({ k, label }) => (
    <th onClick={() => setSortKey(k)}
      style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, color: sortKey === k ? "#22c55e" : "#475569", fontWeight: 600, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
      {label} {sortKey === k ? "▼" : ""}
    </th>
  );

  const PathPanel = ({ title, accent, stages = [] }) => (
    <div style={{ minWidth: 0, border: `1px solid ${accent}44`, borderRadius: 8, padding: 10, background: "#07111f" }}>
      <div style={{ color: accent, fontWeight: 700, fontSize: 11, marginBottom: 9 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {stages.map((stage, index) => (
          <div key={stage.key || index} style={{ display: "grid", gridTemplateColumns: "24px minmax(0, 1fr)", gap: 7, alignItems: "start" }}>
            <span style={{ width: 21, height: 21, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: `${accent}22`, border: `1px solid ${accent}66`, color: accent, fontSize: 9, fontWeight: 700 }}>{index + 1}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#cbd5e1", fontWeight: 600 }}>{stage.label} <span style={{ color: accent, fontWeight: 500 }}>· {String(stage.value || "—")}</span></div>
              {stage.detail && <div style={{ color: "#64748b", marginTop: 1 }}>{stage.detail}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const marketClosed = botStatus?.marketOpen === false;
  const manualAutoPaperDisabled = universeSource !== "nifty500_volume" || marketClosed;
  const lastAutoScan = botStatus?.lastAutoScannerRun;
  const resources = liveResources || botStatus?.resources || null;
  const processMemory = resources?.processMemory || {};
  const systemMemory = resources?.systemMemory || {};
  const storage = resources?.storage || {};
  const resourceAgeSec = Number.isFinite(Number(resources?.cacheAgeMs)) ? Math.round(Number(resources.cacheAgeMs) / 1000) : 0;
  const effectiveDayVolumeFloor = Number(effectiveScanMeta?.diagnostics?.filters?.minVolume ?? minVolume ?? 0);
  const aiEntryGateStatus = botStatus?.advisor?.entryGate || {};
  const aiEntryGateCacheMin = Number.isFinite(Number(aiEntryGateStatus.cacheMs))
    ? Math.round(Number(aiEntryGateStatus.cacheMs) / 60000)
    : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Market status banner */}
      {marketClosed && (
        <div style={{ background: "#1a1000", border: "1px solid #f59e0b44", borderRadius: 8, padding: "10px 14px", fontSize: 11, color: "#f59e0b" }}>
          Market is <strong>CLOSED</strong> (NSE: 09:15-15:30 IST Mon-Fri). Manual Upstox quote scans can still run; auto-entry and live refresh stay market-hours controlled.
          Strategy signals need live ticks, so no new WebSocket strategy candles are formed while market is closed.
        </div>
      )}

      {/* Info */}
      <div style={{ fontSize: 11, color: "#64748b", background: "#060d1a", border: "1px solid #1e293b", borderRadius: 8, padding: "10px 14px", lineHeight: 1.55 }}>
        <strong style={{ color: "#94a3b8" }}>Live Market Scanner</strong> fetches live Upstox quotes for the selected universe.{" "}
        <strong style={{ color: "#94a3b8" }}>Strategy Signals</strong> shows bot strategy output from WebSocket candles (~60 min of ticks needed per symbol).
      </div>

      {resources && (
        <div style={{ fontSize: 11, color: "#64748b", background: "#060d1a", border: "1px solid #1e293b", borderRadius: 8, padding: "10px 14px", lineHeight: 1.55 }}>
          <strong style={{ color: "#94a3b8" }}>Backend resources</strong>{" "}
          <button
            type="button"
            onClick={refreshResources}
            disabled={resourcesLoading}
            style={{ background: "transparent", border: "1px solid #334155", color: resourcesLoading ? "#475569" : "#38bdf8", borderRadius: 6, padding: "3px 8px", cursor: resourcesLoading ? "not-allowed" : "pointer", fontSize: 10 }}
          >
            {resourcesLoading ? "Refreshing..." : "Refresh storage"}
          </button>
          {resourcesError && <span style={{ color: "#f87171" }}> {resourcesError}</span>}{" "}
          RAM RSS <span style={{ color: "#38bdf8" }}>{formatMb(processMemory.rssMb)}</span>
          {" · "}heap <span style={{ color: "#38bdf8" }}>{formatMb(processMemory.heapUsedMb)}/{formatMb(processMemory.heapTotalMb)}</span>
          {" · "}external <span style={{ color: "#38bdf8" }}>{formatMb(processMemory.externalMb)}</span>
          {" · "}system used <span style={{ color: "#38bdf8" }}>{formatPct(systemMemory.usedPct)}</span>
          {storage.filesystem?.available ? (
            <>
              {" · "}disk <span style={{ color: "#38bdf8" }}>{formatMb(storage.filesystem.usedMb)}/{formatMb(storage.filesystem.totalMb)}</span>
              {" "}(<span style={{ color: "#38bdf8" }}>{formatPct(storage.filesystem.usedPct)}</span>)
            </>
          ) : null}
          {" · "}data <span style={{ color: "#38bdf8" }}>{formatMb(storage.dataDir?.mb)}</span>
          {" · "}scanner history cache <span style={{ color: "#38bdf8" }}>{formatMb(storage.scannerHistoryCache?.mb)}</span>
          {storage.scannerHistoryCache?.files != null ? <span>{" "}({storage.scannerHistoryCache.files} files)</span> : null}
          {" · "}derived metrics <span style={{ color: "#38bdf8" }}>{formatMb(storage.scannerDerivedMetricsCache?.mb)}</span>
          {storage.scannerDerivedMetricsCache?.files != null ? <span>{" "}({storage.scannerDerivedMetricsCache.files} files)</span> : null}
          {" · "}5m cache <span style={{ color: "#38bdf8" }}>{formatMb(storage.scannerIntradayActivityCache?.mb)}</span>
          {storage.scannerIntradayActivityCache?.files != null ? <span>{" "}({storage.scannerIntradayActivityCache.files} files)</span> : null}
          {" · "}sample {resourceAgeSec}s old
        </div>
      )}

      {scanMode === "market" && (
        <div style={{ fontSize: 11, color: "#64748b", background: "#060d1a", border: "1px solid #1e293b", borderRadius: 8, padding: "10px 14px", lineHeight: 1.55 }}>
          <strong style={{ color: "#94a3b8" }}>Backend auto scan</strong>{" "}
          {botStatus?.autoTradeFromScanner ? "uses backend .env settings only. Manual controls below are inspection-only and do not change the bot scanner." : "is off in backend .env."}
          <div style={{ marginTop: 6, color: "#475569" }}>
            Last auto scan: {lastAutoScan?.finishedAt ? `${new Date(lastAutoScan.finishedAt).toLocaleString("en-IN")} - ${lastAutoScan.status || "ok"}${lastAutoScan.status === "skipped" && lastAutoScan.reason ? ` (${lastAutoScan.reason})` : ""} - ${lastAutoScan.scanned || 0} rows - ${(lastAutoScan.entries || []).length} entries` : "not run yet in this backend process"}.
          </div>
        </div>
      )}

      {scanMode === "market" && autoStrategy && (
        <div style={{ fontSize: 11, color: "#64748b", background: "#060d1a", border: "1px solid #1e293b", borderRadius: 8, padding: "10px 14px", lineHeight: 1.55 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: showAutoStrategy ? 8 : 0 }}>
            <strong style={{ color: "#e2e8f0" }}>Auto-trading entry, strategy and exit details</strong>
            <span style={{ color: autoStrategy.enabled ? "#22c55e" : "#f59e0b" }}>{autoStrategy.enabled ? "enabled" : "disabled"}</span>
            <span>{autoStrategy.mode}</span>
            <span>interval {Math.round(Number(autoStrategy.schedule?.intervalMs || 0) / 60000)}m</span>
            <button
              type="button"
              onClick={() => setShowAutoStrategy((v) => !v)}
              style={{ marginLeft: "auto", background: "transparent", border: "1px solid #334155", color: "#94a3b8", borderRadius: 6, padding: "4px 9px", cursor: "pointer", fontSize: 10 }}
            >
              {showAutoStrategy ? "Collapse details" : "Expand details"}
            </button>
          </div>
          {showAutoStrategy && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 10, marginBottom: 10 }}>
                <PathPanel title="SCANNER PATH" accent="#38bdf8" stages={autoStrategy.scannerPath || []} />
                <PathPanel title="AUTO-ENTRY PATH" accent="#22c55e" stages={autoStrategy.autoEntryPath || []} />
              </div>
              <div style={{ color: "#94a3b8", fontWeight: 700, marginBottom: 7 }}>AUTO-ENTRY GATES (ENV)</div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                {(autoStrategy.entryGates || []).map((gate) => (
                  <div key={gate.key} style={{ border: "1px solid #1e293b", borderRadius: 7, padding: "7px 8px", background: "#07111f" }}>
                    <div style={{ color: "#cbd5e1", fontWeight: 600 }}>{gate.label}: <span style={{ color: "#22c55e" }}>{String(gate.value)}</span></div>
                    <div style={{ color: "#64748b", marginTop: 2 }}>{gate.reason}</div>
                  </div>
                ))}
              </div>
              <div style={{ color: "#94a3b8", fontWeight: 700, margin: "12px 0 7px" }}>STRATEGIES (ENV)</div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                {(autoStrategy.strategies || []).map((gate) => (
                  <div key={gate.key} style={{ border: "1px solid #1e293b", borderRadius: 7, padding: "7px 8px", background: "#07111f" }}>
                    <div style={{ color: "#cbd5e1", fontWeight: 600 }}>{gate.label}: <span style={{ color: gate.value === "enabled" ? "#22c55e" : "#64748b" }}>{String(gate.value)}</span></div>
                    <div style={{ color: "#64748b", marginTop: 2 }}>{gate.reason}</div>
                  </div>
                ))}
              </div>
              <div style={{ color: "#94a3b8", fontWeight: 700, margin: "12px 0 7px" }}>AUTO-EXIT GATES (ENV + CODE)</div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                {(autoStrategy.exitGates || []).map((gate) => (
                  <div key={gate.key} style={{ border: "1px solid #1e293b", borderRadius: 7, padding: "7px 8px", background: "#07111f" }}>
                    <div style={{ color: "#cbd5e1", fontWeight: 600 }}>{gate.label}: <span style={{ color: "#f59e0b" }}>{String(gate.value)}</span></div>
                    <div style={{ color: "#64748b", marginTop: 2 }}>{gate.reason}</div>
                  </div>
                ))}
              </div>
              {Array.isArray(autoStrategy.notes) && autoStrategy.notes.length > 0 && (
                <div style={{ marginTop: 8, color: "#475569" }}>
                  {autoStrategy.notes.join(" ")}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Watchlist search */}
      <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 8, padding: "12px 14px" }}>
        <div style={{ fontSize: 10, color: "#475569", marginBottom: 8, fontWeight: 600 }}>ADD SYMBOL TO WATCHLIST</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <input
            placeholder="Type NSE symbol: RELIANCE, TCS, HDFCBANK..."
            value={searchQ}
            onChange={e => handleSearch(e.target.value.toUpperCase())}
            style={{ flex: "1 1 220px", minWidth: 0, background: "#07111f", border: "1px solid #1e293b", color: "#e2e8f0", borderRadius: 6, padding: "8px 10px", fontSize: 12, fontFamily: "'DM Mono',monospace", outline: "none" }}
          />
          {searchLoading && <span style={{ fontSize: 10, color: "#475569" }}>Searching...</span>}
          {searchQ && !searchLoading && <button onClick={() => { setSearchQ(""); setSearchResults([]); }} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 14 }}>✕</button>}
        </div>
        {searchResults.length > 0 && (
          <div style={{ marginTop: 6, maxHeight: 200, overflowY: "auto", background: "#020617", border: "1px solid #1e293b", borderRadius: 8 }}>
            {searchResults.map((m, i) => (
              <div key={m.instrumentKey || `${m.symbol}-${i}`} style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", gap: 10, flexWrap: isMobile ? "wrap" : "nowrap", padding: "8px 10px", borderBottom: "1px solid #0a1628" }}>
                <div
                  onClick={() => {
                    onOpenSymbol?.({ symbol: m.symbol, instrumentKey: m.instrumentKey });
                    setSearchQ("");
                    setSearchResults([]);
                  }}
                  style={{ minWidth: 0, cursor: "pointer" }}
                  title="Open chart"
                >
                  <strong style={{ color: "#e2e8f0", fontSize: 12 }}>{m.symbol}</strong>
                  {m.name && <span style={{ color: "#64748b", fontSize: 11, marginLeft: 8 }}>{m.name}</span>}
                  <span style={{ color: "#1e293b", fontSize: 9, marginLeft: 8, fontFamily: "monospace" }}>{m.instrumentKey}</span>
                </div>
                <button onClick={() => { addToWatchlist(m.symbol, m.instrumentKey); setSearchQ(""); setSearchResults([]); }}
                  disabled={addingSymbol === m.symbol}
                  style={{ background: "#052e16", border: "1px solid #22c55e66", color: "#22c55e", borderRadius: 6, padding: "5px 12px", fontSize: 10, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {addingSymbol === m.symbol ? "Adding..." : "+ Add"}
                </button>
              </div>
            ))}
          </div>
        )}
        {searchQ.length > 0 && !searchLoading && searchResults.length === 0 && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#475569" }}>No matches for "{searchQ}". Try a shorter prefix (e.g. "REL" for RELIANCE).</div>
        )}
      </div>

      {/* Scan controls */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <select value={scanMode} onChange={(e) => { setScanMode(e.target.value); setScanError(null); }} style={selectStyle}>
          <option value="market">Live market scanner</option>
          <option value="signals">Strategy signals</option>
        </select>

        {scanMode === "market" && (
          <>
            <select value={direction} onChange={(e) => { touchManualControls(); setDirection(e.target.value); }} style={selectStyle}>
              <option value="all">All moves</option>
              <option value="up">Trending up ↑</option>
              <option value="down">Trending down ↓</option>
              <option value="neutral">Neutral</option>
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={trendingOnly} onChange={(e) => { touchManualControls(); setTrendingOnly(e.target.checked); }} />
              Trending only
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={syncBackendDefaults}
                onChange={(e) => {
                  const checked = e.target.checked;
                  manualControlsTouched.current = !checked;
                  setSyncBackendDefaults(checked);
                }}
              />
              Sync backend defaults
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={autoPaper} disabled={manualAutoPaperDisabled} onChange={(e) => { touchManualControls(); setAutoPaper(e.target.checked); }} />
              Auto paper entries{marketClosed ? " (market closed)" : ""}
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={autoRefresh} onChange={(e) => { touchManualControls(); setAutoRefresh(e.target.checked); }} />
              Live refresh
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={applyScanner} onChange={(e) => { touchManualControls(); setApplyScanner(e.target.checked); }} />
              Apply our scanner
            </label>
            <select value={screenerMode} onChange={(e) => { touchManualControls(); setScreenerMode(e.target.value); }} style={selectStyle}>
              <option value="blend">Universe + screeners</option>
              <option value="only">Screeners only</option>
              <option value="off">Universe only</option>
            </select>
            {[
              ["intraday_recovery", "Recovery"],
              ["momentum_burst", "Momentum"],
              ["bullish_crossover", "Crossover"],
              ["open_low", "Open=Low"],
            ].map(([key, label]) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 5, color: "#94a3b8", fontSize: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!!intradayScreeners[key]}
                  disabled={screenerMode === "off"}
                  onChange={(e) => { touchManualControls(); setIntradayScreeners((prev) => ({ ...prev, [key]: e.target.checked })); }}
                />
                {label}
              </label>
            ))}
            <label style={inputWrapStyle}>
              Scan size
              <input type="number" min="50" max="2000" value={universeLimit}
                onChange={(e) => {
                  touchManualControls();
                  universeLimitTouched.current = true;
                  const value = Number(e.target.value);
                  setUniverseLimit(Number.isFinite(value) && value > 0 ? Math.max(50, value) : "");
                }}
                placeholder={String(botStatus?.trendingUniverseLimit || 1000)}
                style={smallInputStyle} />
            </label>
            <label style={inputWrapStyle}>
              Scan from
            <select value={universeSource} onChange={(e) => { touchManualControls(); setUniverseSource(e.target.value); }} style={selectStyle}>
              <option value="nifty500_volume">All sources</option>
              <option value="nifty500">Nifty 500 only</option>
              <option value="external">GitHub/Yahoo only</option>
              <option value="chartink">Chartink only</option>
              <option value="upstox500">Upstox 500 only</option>
              <option value="watchlist">Manual watchlist</option>
              <option value="bod">BOD slice only</option>
            </select>
            </label>
            {universeSource === "nifty500_volume" && (
              <span style={{ color: "#64748b", fontSize: 10 }}>
                Nifty + GitHub/Yahoo + Chartink + Upstox 500 + BOD
              </span>
            )}
            <label style={inputWrapStyle}>
              Min %
              <input type="number" min="0" step="0.25" value={minChangePct}
                onChange={(e) => { touchManualControls(); setMinChangePct(Number(e.target.value) || 0); }}
                style={smallInputStyle} />
            </label>
            <label style={inputWrapStyle}>
              Max %
              <input type="number" min="0" step="0.25" value={maxChangePct}
                onChange={(e) => { touchManualControls(); setMaxChangePct(Number(e.target.value) || 0); }}
                style={smallInputStyle} />
            </label>
            <label style={inputWrapStyle}>
              Min price
              <input type="number" min="0" step="50" value={minStockPrice}
                onChange={(e) => { touchManualControls(); setMinStockPrice(Number(e.target.value) || 0); }}
                style={smallInputStyle} />
            </label>
            <label style={inputWrapStyle}>
              Day vol
              <input type="number" min="0" step="10000" value={minVolume}
                disabled={dynamicMinVolume}
                onChange={(e) => { touchManualControls(); setMinVolume(Number(e.target.value) || 0); }}
                style={{ ...smallInputStyle, opacity: dynamicMinVolume ? 0.55 : 1 }} />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={dynamicMinVolume} onChange={(e) => { touchManualControls(); setDynamicMinVolume(e.target.checked); }} />
              Dynamic day vol
            </label>
            <span style={{ color: "#64748b", fontSize: 10 }}>
              {dynamicMinVolume ? "Dynamic day vol" : "Fixed day vol"} {Number(effectiveDayVolumeFloor || 0).toLocaleString("en-IN")}
            </span>
            <label style={inputWrapStyle}>
              Last 5m vol
              <input type="number" min="0" step="1000" value={minLastCandleVolume}
                onChange={(e) => { touchManualControls(); setMinLastCandleVolume(Number(e.target.value) || 0); }}
                style={smallInputStyle} />
            </label>
            <label style={inputWrapStyle}>
              Min score
              <input type="number" min="0" step="0.1" value={minTrendScore}
                onChange={(e) => { touchManualControls(); setMinTrendScore(Number(e.target.value) || 0); }}
                style={smallInputStyle} />
            </label>
            <label style={inputWrapStyle}>
              Min RVOL
              <input type="number" min="0" step="0.1" value={minRelativeVolume}
                onChange={(e) => { touchManualControls(); setMinRelativeVolume(Number(e.target.value) || 0); }}
                style={smallInputStyle} />
            </label>
            <label style={inputWrapStyle}>
              Min 20d avg
              <input type="number" min="0" step="50000" value={minAverageVolume}
                onChange={(e) => { touchManualControls(); setMinAverageVolume(Number(e.target.value) || 0); }}
                style={{ ...smallInputStyle, width: 96 }} />
            </label>
            <select value={rsiMode} onChange={(e) => { touchManualControls(); setRsiMode(e.target.value); }} style={selectStyle}>
              <option value="all">Any RSI</option>
              <option value="bullish">RSI 45-70</option>
              <option value="avoid_overbought">Avoid RSI &gt;70</option>
              <option value="oversold">Oversold RSI</option>
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={macdPositive} onChange={(e) => { touchManualControls(); setMacdPositive(e.target.checked); }} />
              MACD+
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={mercuryPositive} onChange={(e) => { touchManualControls(); setMercuryPositive(e.target.checked); }} />
              Mercury+
            </label>
            <label style={inputWrapStyle}>
              Hist top
              <input type="number" min="200" max="1000" value={scoreHistoryLimit}
                onChange={(e) => { touchManualControls(); setScoreHistoryLimit(Math.max(200, Number(e.target.value) || 200)); }}
                style={smallInputStyle} />
            </label>
          </>
        )}

        <button
          onClick={runScan} disabled={scanning}
          style={{
            background: scanning ? "#0f172a" : "linear-gradient(135deg,#22c55e,#16a34a)",
            border: "none", color: scanning ? "#475569" : "#fff",
            padding: "10px 20px", borderRadius: 8, cursor: scanning ? "not-allowed" : "pointer",
            fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 600,
          }}
        >
          {scanning ? `${scanProgressText || "Scanning"}... ${progress}%` : scanMode === "market" ? "▶ Run Live Scanner" : "▶ Run Strategy Signals"}
        </button>

        {scanMode === "market" && (
          <button
            type="button"
            onClick={syncBackendScanRows}
            disabled={!backendAutoRows.length}
            title={backendAutoRows.length ? "Copy latest scheduled backend autoscan rows into this scanner table" : "No backend autoscan rows available yet"}
            style={{ background: "transparent", border: "1px solid #38bdf866", color: backendAutoRows.length ? "#38bdf8" : "#475569", borderRadius: 6, padding: "8px 12px", cursor: backendAutoRows.length ? "pointer" : "not-allowed", fontSize: 11 }}
          >
            Sync backend scan
          </button>
        )}

        {results.length > 0 && resultsSource !== "backend" && (
          <button onClick={() => { setResults([]); setResultsSource(null); setCachedScanSavedAt(null); setScanError(null); setScanInfo(null); setScanMeta(null); try { window.localStorage.removeItem(SCANNER_CACHE_KEY); } catch {} }}
            style={{ background: "transparent", border: "1px solid #334155", color: "#475569", borderRadius: 6, padding: "8px 12px", cursor: "pointer", fontSize: 11 }}>
            Clear manual
          </button>
        )}

        <div style={{ marginLeft: isMobile ? 0 : "auto", display: "flex", gap: 6, overflowX: "auto", width: isMobile ? "100%" : "auto", paddingBottom: isMobile ? 2 : 0 }}>
          {["ALL", "TRENDING", "BUY_ALL", "HOLD", "SELL_ALL"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              style={{
                background: filter === f ? "#0d1f35" : "transparent",
                border: `1px solid ${filter === f ? "#22c55e" : "#1e293b"}`,
                color: filter === f ? "#22c55e" : "#475569",
                borderRadius: 6, padding: "5px 10px", cursor: "pointer",
                fontFamily: "'DM Mono',monospace", fontSize: 10, fontWeight: 600,
              }}>
              {f.replace("_ALL", "")}
            </button>
          ))}
        </div>
      </div>

      {/* Progress bar */}
      {effectiveScanMeta?.diagnostics?.externalUniverse?.enabled && (() => {
        const feed = effectiveScanMeta.diagnostics.externalUniverse;
        const issue = feed.error || feed.warning;
        return <div role="status" style={{ fontSize: 11, color: issue ? "#fbbf24" : "#38bdf8", overflowWrap: "anywhere" }}>
          GitHub/Yahoo: {issue || `${feed.resolvedCount || 0} symbols resolved`}
          {feed.generatedAt && ` | Generated ${new Date(feed.generatedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST`}
          {feed.cacheHit && " | Cached"}
        </div>;
      })()}
      {scanning && (
        <div>
          <div style={{ background: "#0f172a", borderRadius: 4, height: 4 }}>
            <div style={{ width: `${progress}%`, height: 4, background: "linear-gradient(90deg,#22c55e,#0ea5e9)", borderRadius: 4, transition: "width 0.2s" }} />
          </div>
          {scanProgressText && (
            <div style={{ marginTop: 6, fontSize: 10, color: "#64748b" }}>
              {scanProgressText}
            </div>
          )}
        </div>
      )}

      {/* Status line */}
      <div style={{ fontSize: 11, color: "#334155" }}>
        {hasScanned
          ? <>{showingBackendAutoScan ? "Backend autoscan" : "Manual scan"} returned {effectiveResults.length} row{effectiveResults.length !== 1 ? "s" : ""} from the backend max of {limit}; {sorted.length} visible after page filters. Increase backend `SCANNER_LIMIT` to return more rows, or relax filters if fewer rows passed.{autoRefresh && botStatus?.marketOpen && scanMode === "market" ? " Live refresh every 60s." : ""}</>
          : manualWatchlistRows.length > 0
            ? <span style={{ color: "#64748b" }}>{manualWatchlistRows.length} manual watchlist item{manualWatchlistRows.length !== 1 ? "s" : ""} — click Scan for live data</span>
            : <span>Click <strong style={{ color: "#22c55e" }}>▶ Run Live Scanner</strong> to fetch live NSE quotes</span>
        }
      </div>

      {scanMode === "market" && (
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
          {[
            ["rows", `Scanner rows (${sorted.length})`],
            ["ai", `AI gate status (${aiGateRows.length})`],
            ["aiHistory", `Past AI gate scans (${aiGateHistoryMeta?.total ?? aiGateHistory.length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setResultView(key)}
              style={{
                background: resultView === key ? "#0d1f35" : "transparent",
                border: `1px solid ${resultView === key ? "#38bdf8" : "#1e293b"}`,
                color: resultView === key ? "#38bdf8" : "#64748b",
                borderRadius: 7,
                padding: "7px 10px",
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {resultView === "ai" && scanMode === "market" && aiEntryGateStatus.enabled && (
        <div style={{ fontSize: 11, color: "#64748b", background: "#060d1a", border: "1px solid #1e293b", borderRadius: 8, padding: "8px 12px", lineHeight: 1.5 }}>
          The auto-entry path sends candidate rows to AI after deterministic filters, capped by SCANNER_AUTO_TRADE_MAX_ENTRIES. {aiEntryGateCacheMin > 0 ? `WAIT/REJECT and approved decisions are skipped for ${aiEntryGateCacheMin} min so later scans can advance to other candidates without re-burning Workers AI quota.` : "AI decision cache is disabled."}
        </div>
      )}

      {/* Error / Info */}
      {scanError && (
        <div style={{ fontSize: 12, color: "#f87171", background: "#1f0a0a", border: "1px solid #7f1d1d44", borderRadius: 8, padding: "10px 14px", lineHeight: 1.6 }}>
          ⚠️ {scanError}
        </div>
      )}
      {scanInfo && !scanError && (
        <div style={{ fontSize: 11, color: "#22c55e", background: "#052e1622", border: "1px solid #22c55e33", borderRadius: 8, padding: "8px 12px" }}>
          ✓ {scanInfo}
          {effectiveScanMeta?.diagnostics?.enrichedCount === 0 && (
            <div style={{ color: "#f59e0b", marginTop: 4 }}>
              Historical enrichment did not return usable candles for this scan, so RSI/RVOL/Tech may be blank. Try a smaller Hist top or reconnect Upstox if this repeats.
            </div>
          )}
        </div>
      )}

      {contextStatus && (
        <div style={{ fontSize: 11, color: contextStatus.type === "ok" ? "#22c55e" : "#f87171", background: contextStatus.type === "ok" ? "#052e1622" : "#1f0a0a", border: `1px solid ${contextStatus.type === "ok" ? "#22c55e33" : "#7f1d1d44"}`, borderRadius: 8, padding: "8px 12px" }}>
          {contextStatus.text}
        </div>
      )}

      {/* Manual price entry for paper buy without live price */}
      {manualPriceSymbol && (
        <div style={{ background: "#07111f", border: "1px solid #38bdf866", borderRadius: 10, padding: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#38bdf8" }}>Paper buy <strong>{manualPriceSymbol}</strong> — enter price manually:</span>
          <input type="number" min="0.01" step="0.01" value={manualPrice}
            onChange={e => setManualPrice(e.target.value)}
            placeholder="e.g. 2890.50" autoFocus
            style={{ width: 130, background: "#020617", border: "1px solid #38bdf844", color: "#e2e8f0", borderRadius: 6, padding: "7px 10px", fontSize: 12, fontFamily: "'DM Mono',monospace", outline: "none" }}
          />
          <button
            disabled={!manualPrice || Number(manualPrice) <= 0 || paperBuying === manualPriceSymbol}
            onClick={() => { const row = effectiveResults.find(r => r.symbol === manualPriceSymbol); if (row) paperBuy(row, Number(manualPrice)); }}
            style={{ background: "#0d1f35", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "7px 14px", fontSize: 11, cursor: "pointer" }}>
            {paperBuying === manualPriceSymbol ? "Buying..." : "Confirm Buy"}
          </button>
          <button onClick={() => setManualPriceSymbol(null)} style={{ background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>
      )}

      {/* Empty state (no manual watchlist) */}
      {!hasScanned && manualWatchlistRows.length === 0 && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, padding: isMobile ? 24 : 50, textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>⟳</div>
          <div style={{ color: "#334155", fontSize: 13, marginBottom: 10 }}>No scan results yet</div>
          <div style={{ color: "#475569", fontSize: 12, lineHeight: 1.8 }}>
            Click <strong style={{ color: "#22c55e" }}>▶ Run Live Scanner</strong> to fetch live quotes from Upstox NSE universe.<br />
            Use the search box above to add specific symbols to your watchlist.
            {marketClosed && <><br /><span style={{ color: "#f59e0b" }}>Market is closed — live scan returns data during 09:15–15:30 IST.</span></>}
          </div>
        </div>
      )}

      {/* Results table */}
      {resultView === "ai" && scanMode === "market" && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, overflow: "auto" }}>
          {aiGateRows.length > 0 ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 900 }}>
              <thead>
                <tr style={{ background: "#060d1a", borderBottom: "1px solid #0f172a" }}>
                  <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>SYMBOL</th>
                  <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>STATUS</th>
                  <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>DECISION</th>
                  <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>CONF</th>
                  <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>PROVIDER</th>
                  <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>CANDLES</th>
                  <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>AI ANALYSIS / REASON</th>
                </tr>
              </thead>
              <tbody>
                {aiGateRows.map((row, index) => {
                  const status = String(row.status || "").toLowerCase();
                  const statusColor = status.includes("approved") || status.includes("bought") ? "#22c55e" : "#f59e0b";
                  const stats = row.contextStats || {};
                  const candleText = stats.dailyCandles != null
                    ? `1d ${stats.dailyCandles}, 1h ${stats.hourlyCandles}, 15m ${stats.fifteenMinuteCandles}, 5m ${stats.fiveMinuteCandles}`
                    : "-";
                  return (
                    <tr key={`${row.symbol}-${index}`} style={{ borderBottom: "1px solid #060d1a", background: index % 2 === 0 ? "#060d1a" : "#07111f" }}>
                      <td style={{ padding: "11px 12px", fontWeight: 700, color: "#38bdf8", fontFamily: "'Syne',sans-serif" }}>{row.symbol}</td>
                      <td style={{ padding: "11px 12px", color: statusColor, fontWeight: 700 }}>{String(row.status || "-").replace(/_/g, " ").toUpperCase()}</td>
                      <td style={{ padding: "11px 12px", color: row.decision === "BUY" ? "#22c55e" : "#f59e0b", fontWeight: 700 }}>{row.decision || "-"}</td>
                      <td style={{ padding: "11px 12px", color: "#cbd5e1" }}>{Number.isFinite(Number(row.confidence)) ? `${Number(row.confidence).toFixed(0)}%` : "-"}</td>
                      <td style={{ padding: "11px 12px", color: "#64748b", maxWidth: 180 }}>
                        <div>{row.provider || "-"}</div>
                        {row.model && <div style={{ color: "#475569", fontSize: 10 }}>{row.model}</div>}
                      </td>
                      <td style={{ padding: "11px 12px", color: "#64748b", whiteSpace: "nowrap" }}>{candleText}</td>
                      <td style={{ padding: "11px 12px", color: "#cbd5e1", minWidth: 280, lineHeight: 1.45 }}>
                        {row.reason || "-"}
                        {row.analyzedAt && <div style={{ color: "#475569", fontSize: 10, marginTop: 4 }}>{new Date(row.analyzedAt).toLocaleString("en-IN")}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 18, color: "#64748b", fontSize: 12 }}>
              No AI gate attempt is recorded for this scan. It appears only when auto-entry is requested, the market/session gates allow entry evaluation, and at least one scanner row reaches the AI pre-buy gate.
            </div>
          )}
        </div>
      )}

      {resultView === "aiHistory" && scanMode === "market" && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", padding: "10px 12px", borderBottom: "1px solid #0f172a", flexWrap: "wrap" }}>
            <div style={{ color: "#94a3b8", fontSize: 11 }}>
              Stored server-side in DATA_DIR. Showing latest {aiGateHistory.length} of {aiGateHistoryMeta?.total ?? aiGateHistory.length}.
              {aiGateHistoryMeta?.updatedAt && <span style={{ color: "#475569" }}> Updated {new Date(aiGateHistoryMeta.updatedAt).toLocaleString("en-IN")}</span>}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" onClick={loadAiGateHistory} disabled={aiGateHistoryLoading} style={{ background: "#0d1f35", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "6px 10px", fontSize: 11, cursor: aiGateHistoryLoading ? "wait" : "pointer" }}>
                {aiGateHistoryLoading ? "Loading..." : "Refresh"}
              </button>
              <button type="button" onClick={exportAiGateHistory} disabled={aiGateHistoryExporting || aiGateHistoryLoading || (aiGateHistoryMeta?.total ?? aiGateHistory.length) === 0} style={{ background: "#0d1f35", border: "1px solid #22c55e66", color: "#22c55e", borderRadius: 6, padding: "6px 10px", fontSize: 11, cursor: aiGateHistoryExporting || aiGateHistoryLoading || (aiGateHistoryMeta?.total ?? aiGateHistory.length) === 0 ? "not-allowed" : "pointer" }}>
                {aiGateHistoryExporting ? "Exporting..." : "Export CSV"}
              </button>
              <button type="button" onClick={clearAiGateHistory} disabled={aiGateHistoryLoading || aiGateHistory.length === 0} style={{ background: "#2d0a0a", border: "1px solid #ef444466", color: "#f87171", borderRadius: 6, padding: "6px 10px", fontSize: 11, cursor: aiGateHistoryLoading || aiGateHistory.length === 0 ? "not-allowed" : "pointer" }}>
                Clear all
              </button>
            </div>
          </div>
          {aiGateHistoryError && (
            <div style={{ padding: 12, color: "#f87171", fontSize: 12, background: "#1f0a0a" }}>{aiGateHistoryError}</div>
          )}
          {aiGateHistory.length > 0 ? (
            <div style={{ overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1120 }}>
                <thead>
                  <tr style={{ background: "#060d1a", borderBottom: "1px solid #0f172a" }}>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>SYMBOL</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>STATUS</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>OUTCOME</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>DECISION</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>CONF</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>PROVIDER</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>SCANNER</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>CANDLES</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>AI ANALYSIS</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>TIME</th>
                    <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {aiGateHistory.map((row, index) => {
                    const status = String(row.status || "").toLowerCase();
                    const statusColor = status.includes("entry") ? "#22c55e" : status.includes("error") ? "#ef4444" : "#f59e0b";
                    const stats = row.contextStats || {};
                    const scanner = row.scanner || {};
                    const candleText = stats.dailyCandles != null
                      ? `1d ${stats.dailyCandles || 0}, 1h ${stats.hourlyCandles || 0}, 15m ${stats.fifteenMinuteCandles || 0}, 5m ${stats.fiveMinuteCandles || 0}`
                      : "-";
                    return (
                      <tr key={row.id || `${row.symbol}-${index}`} style={{ borderBottom: "1px solid #060d1a", background: index % 2 === 0 ? "#060d1a" : "#07111f" }}>
                        <td style={{ padding: "11px 12px", fontWeight: 700, color: "#38bdf8", fontFamily: "'Syne',sans-serif" }}>{row.symbol}</td>
                        <td style={{ padding: "11px 12px", color: statusColor, fontWeight: 700 }}>{String(row.status || "-").replace(/_/g, " ").toUpperCase()}</td>
                        <td style={{ padding: "11px 12px", color: "#cbd5e1", fontSize: 11 }}>{String(row.outcome || "-").replace(/_/g, " ")}</td>
                        <td style={{ padding: "11px 12px", color: row.decision === "BUY" ? "#22c55e" : "#f59e0b", fontWeight: 700 }}>{row.decision || "-"}</td>
                        <td style={{ padding: "11px 12px", color: "#cbd5e1" }}>{Number.isFinite(Number(row.confidence)) ? `${Number(row.confidence).toFixed(0)}%` : "-"}</td>
                        <td style={{ padding: "11px 12px", color: "#64748b", maxWidth: 170 }}>
                          <div>{row.provider || "-"}</div>
                          {row.model && <div style={{ color: "#475569", fontSize: 10 }}>{row.model}</div>}
                        </td>
                        <td style={{ padding: "11px 12px", color: "#94a3b8", minWidth: 160, lineHeight: 1.45 }}>
                          <div>Score {scanner.score ?? "-"}</div>
                          <div>Chg {Number.isFinite(Number(scanner.changePct)) ? `${Number(scanner.changePct).toFixed(2)}%` : "-"}</div>
                          <div>RVOL {Number.isFinite(Number(scanner.relativeVolume)) ? `${Number(scanner.relativeVolume).toFixed(2)}x` : "-"}</div>
                        </td>
                        <td style={{ padding: "11px 12px", color: "#64748b", whiteSpace: "nowrap" }}>{candleText}</td>
                        <td style={{ padding: "11px 12px", color: "#cbd5e1", minWidth: 320, lineHeight: 1.45 }}>
                          {row.reason || row.error || "-"}
                          {Array.isArray(row.riskFlags) && row.riskFlags.length > 0 && <div style={{ color: "#f59e0b", fontSize: 10, marginTop: 4 }}>Risk: {row.riskFlags.join(", ")}</div>}
                        </td>
                        <td style={{ padding: "11px 12px", color: "#64748b", whiteSpace: "nowrap" }}>{row.createdAt ? new Date(row.createdAt).toLocaleString("en-IN") : "-"}</td>
                        <td style={{ padding: "11px 12px" }}>
                          <button type="button" onClick={() => removeAiGateHistoryRow(row.id)} style={{ background: "transparent", border: "1px solid #ef444466", color: "#f87171", borderRadius: 6, padding: "5px 8px", fontSize: 10, cursor: "pointer" }}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 18, color: "#64748b", fontSize: 12 }}>
              {aiGateHistoryLoading ? "Loading past AI gate scans..." : "No past AI gate scans stored yet."}
            </div>
          )}
        </div>
      )}

      {resultView === "rows" && sorted.length > 0 && (
        <div style={{ background: "#060d1a", border: "1px solid #1e293b", borderRadius: 12, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1200 }}>
            <thead>
              <tr style={{ background: "#060d1a", borderBottom: "1px solid #0f172a" }}>
                <Th k="symbol" label="SYMBOL" />
                <Th k="price" label="PRICE" />
                <Th k="changePct" label="CHG %" />
                <Th k="volume" label="DAY VOL" />
                <Th k="lastCandleVolume" label="5M VOL" />
                <Th k="relativeVolume" label="Adj RVOL" />
                <Th k="rsi14" label="RSI" />
                <Th k="score" label="SCORE" />
                <Th k="action" label="SIGNAL" />
                <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>TECH</th>
                <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>TREND</th>
                <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>REASON</th>
                <th style={{ padding: "10px 12px", fontSize: 10, color: "#475569", fontWeight: 600, textAlign: "left" }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={`${r.symbol}-${r.instrumentKey || i}`} style={{ borderBottom: "1px solid #060d1a", background: i % 2 === 0 ? "#060d1a" : "#07111f" }}>
                  <td
                    style={{ padding: "11px 12px", fontWeight: 700, color: "#38bdf8", fontFamily: "'Syne',sans-serif", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => onOpenSymbol?.({ symbol: r.symbol, instrumentKey: r.instrumentKey })}
                  >
                    {r.symbol}
                  </td>
                  <td style={{ padding: "11px 12px", color: "#cbd5e1" }}>
                    {r.price != null && r.price > 0 ? `₹${Number(r.price).toFixed(2)}` : <span style={{ color: "#334155" }}>—</span>}
                  </td>
                  <td style={{ padding: "11px 12px", fontWeight: 700, color: r.changePct == null ? "#334155" : Number(r.changePct) >= 0 ? "#22c55e" : "#ef4444" }}>
                    {r.changePct != null ? `${Number(r.changePct) >= 0 ? "+" : ""}${Number(r.changePct).toFixed(2)}%` : "—"}
                  </td>
                  <td style={{ padding: "11px 12px", color: "#94a3b8" }}>
                    {r.volume != null && r.volume > 0 ? Number(r.volume).toLocaleString("en-IN") : <span style={{ color: "#334155" }}>—</span>}
                  </td>
                  <td style={{ padding: "11px 12px", color: Number(r.lastCandleVolume || 0) >= minLastCandleVolume ? "#22c55e" : "#64748b", fontWeight: 700 }}>
                    {r.lastCandleVolume != null ? Number(r.lastCandleVolume).toLocaleString("en-IN") : <span style={{ color: "#334155" }}>—</span>}
                  </td>
                  <td
                    title={r.relativeVolumeRaw != null ? `Raw full-day RVOL: ${Number(r.relativeVolumeRaw).toFixed(2)}x` : "Time-adjusted RVOL"}
                    style={{ padding: "11px 12px", color: Number(r.relativeVolume || 0) >= 1.5 ? "#22c55e" : "#64748b", fontWeight: 700 }}
                  >
                    {r.relativeVolume != null ? `${Number(r.relativeVolume).toFixed(2)}x` : "—"}
                  </td>
                  <td style={{ padding: "11px 12px", color: r.rsi14 == null ? "#334155" : r.rsi14 > 70 ? "#f59e0b" : r.rsi14 >= 45 ? "#22c55e" : "#64748b" }}>
                    {r.rsi14 != null ? Number(r.rsi14).toFixed(1) : "—"}
                  </td>
                  <td style={{ padding: "11px 12px", fontWeight: 700, color: (r.score || 0) > 0 ? "#22c55e" : (r.score || 0) < 0 ? "#ef4444" : "#475569" }}>
                    {(r.score || 0) > 0 ? "+" : ""}{r.score || 0}
                  </td>
                  <td style={{ padding: "11px 12px" }}><SignalBadge action={r.action || "HOLD"} /></td>
                  <td style={{ padding: "11px 12px" }}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {r.macdPositive && <span style={{ fontSize: 9, color: "#38bdf8", border: "1px solid #38bdf866", borderRadius: 4, padding: "2px 5px" }}>MACD</span>}
                      {r.mercuryPositive && <span style={{ fontSize: 9, color: "#22c55e", border: "1px solid #22c55e66", borderRadius: 4, padding: "2px 5px" }}>MERC</span>}
                      {!r.macdPositive && !r.mercuryPositive && <span style={{ color: "#334155" }}>—</span>}
                    </div>
                  </td>
                  <td style={{ padding: "11px 12px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                      <span style={{ fontSize: 9, color: r.isTrending ? "#22c55e" : "#64748b", border: `1px solid ${r.isTrending ? "#22c55e66" : "#334155"}`, borderRadius: 4, padding: "3px 6px", whiteSpace: "nowrap" }}>
                        {r.recommendation || "NEUTRAL"}
                      </span>
                      {(r.screenerLabels || []).length > 0 && (
                        <span title={(r.screenerLabels || []).join(", ")} style={{ fontSize: 9, color: "#f59e0b", border: "1px solid #f59e0b66", borderRadius: 4, padding: "3px 6px", whiteSpace: "nowrap" }}>
                          {visibleScreenerLabel(r)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: "11px 12px", color: "#64748b", maxWidth: 260, fontSize: 11, lineHeight: 1.4 }}>
                    {[
                      ...(r.screenerLabels || []).map((label) => formatScreenerLabel(r, label)),
                      ...(r.reasons || [r.reason || ""]),
                    ].filter(Boolean).join(" | ")}
                  </td>
                  <td style={{ padding: "11px 12px" }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
                      <button type="button" disabled={!r.symbol || contextAddingSymbol === r.symbol}
                        onClick={() => addScannerRowToContext(r)}
                        style={{ background: "#111827", border: "1px solid #a78bfa66", color: "#a78bfa", borderRadius: 6, padding: "5px 8px", fontSize: 10, cursor: contextAddingSymbol === r.symbol ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                        {contextAddingSymbol === r.symbol ? "Adding..." : "Add ctx"}
                      </button>
                      <button type="button" disabled={!r.symbol || addingSymbol === r.symbol}
                        onClick={() => addToWatchlist(r.symbol, r.instrumentKey)}
                        style={{ background: "#052e16", border: "1px solid #22c55e66", color: "#22c55e", borderRadius: 6, padding: "5px 8px", fontSize: 10, cursor: addingSymbol === r.symbol ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                        {addingSymbol === r.symbol ? "Adding..." : "+ Watch"}
                      </button>
                      {botStatus?.paperTrading && (
                        <button type="button" disabled={paperBuying === r.symbol}
                          onClick={() => paperBuy(r)}
                          style={{ background: "#0d1f35", border: "1px solid #38bdf866", color: "#38bdf8", borderRadius: 6, padding: "5px 8px", fontSize: 10, cursor: paperBuying === r.symbol ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                          {paperBuying === r.symbol ? "Buying..." : "Paper Buy"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
